import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { knowledgeBaseApi } from '@/api/knowledgeBase';
import { useToast } from '@/hooks/useToast';
export function useKnowledgeBase({ clientCompanyId, workspaceId, campaignId, level, }) {
    const { showSuccess, showError } = useToast();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeEntry, setActiveEntry] = useState(null);
    const [entryLoading, setEntryLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const hasLoadedOnce = useRef(false);
    // Optimistically patch a single category in the local summary state
    // so the UI updates instantly without a full refetch.
    const patchCategoryStatus = useCallback((category, patch) => {
        setSummary(prev => {
            if (!prev)
                return prev;
            return {
                ...prev,
                Categories: prev.Categories.map(c => c.Category === category ? { ...c, ...patch } : c),
            };
        });
    }, []);
    // Derive summary-level fields from a full entry response.
    const deriveCategoryPatch = useCallback((entry) => {
        const hasColorPalette = entry.ColorPalette && Object.values(entry.ColorPalette).some(v => !!v);
        const hasContent = !!(entry.TextContent?.trim())
            || !!(entry.DropdownValue?.trim())
            || hasColorPalette
            || entry.Links.length > 0
            || entry.Files.length > 0;
        return {
            IsPopulated: hasContent,
            HasFiles: entry.Files.length > 0,
            FileCount: entry.Files.length,
        };
    }, []);
    // Silent refresh: updates summary badges without blanking the page.
    // Only the initial load shows the full-page spinner.
    const fetchSummary = useCallback(async () => {
        const isInitialLoad = !hasLoadedOnce.current;
        if (isInitialLoad) {
            setLoading(true);
        }
        setError(null);
        let res;
        if (level === 'Company') {
            res = await knowledgeBaseApi.getCompanySummary(clientCompanyId);
        }
        else if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.getWorkspaceSummary(clientCompanyId, workspaceId);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.getCampaignSummary(clientCompanyId, workspaceId, campaignId);
        }
        if (res?.data) {
            setSummary(res.data);
            hasLoadedOnce.current = true;
        }
        else if (isInitialLoad) {
            setError(res?.error || 'Failed to load knowledge base');
        }
        if (isInitialLoad) {
            setLoading(false);
        }
    }, [clientCompanyId, workspaceId, campaignId, level]);
    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);
    const fetchEntry = useCallback(async (category) => {
        setEntryLoading(true);
        let res;
        if (level === 'Company') {
            res = await knowledgeBaseApi.getCompanyEntry(clientCompanyId, category);
        }
        else if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.getWorkspaceEntry(clientCompanyId, workspaceId, category);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.getCampaignEntry(clientCompanyId, workspaceId, campaignId, category);
        }
        setEntryLoading(false);
        if (res?.data) {
            setActiveEntry(res.data);
            return res.data;
        }
        else if (!res || res.status === 404 || res.error === 'Resource not found') {
            // No entry exists yet for this category (or no API call was made) — show empty state
            // Find the category config from the summary so the UI knows field capabilities
            const catStatus = summary?.Categories.find(c => c.Category === category);
            const emptyEntry = {
                Id: '',
                ClientCompanyId: clientCompanyId,
                SettingsLevel: level,
                TargetId: '',
                Category: category,
                CategoryDisplayName: catStatus?.CategoryDisplayName ?? category,
                InheritFromParent: true,
                Links: [],
                Files: [],
                IsAiGenerated: false,
                IsUserReviewed: false,
                IsInherited: false,
                CategoryConfig: catStatus?.Config ?? {
                    Category: category,
                    DisplayName: category,
                    Description: '',
                    SupportsText: true,
                    SupportsUpload: true,
                    SupportsLinks: false,
                    SupportsDropdown: false,
                    AiPopulatable: false,
                    CampaignOverridable: true,
                },
            };
            setActiveEntry(emptyEntry);
            return emptyEntry;
        }
        else {
            showError('Error', 'Failed to load category details');
            return null;
        }
    }, [clientCompanyId, workspaceId, campaignId, level, showError, summary]);
    const upsertEntry = useCallback(async (category, dto) => {
        setSaving(true);
        let res;
        if (level === 'Company') {
            res = await knowledgeBaseApi.upsertCompanyEntry(clientCompanyId, category, dto);
        }
        else if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.upsertWorkspaceEntry(clientCompanyId, workspaceId, category, dto);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.upsertCampaignEntry(clientCompanyId, workspaceId, campaignId, category, dto);
        }
        setSaving(false);
        if (res?.data) {
            setActiveEntry(res.data);
            showSuccess('Saved', 'Knowledge base entry saved');
            patchCategoryStatus(category, deriveCategoryPatch(res.data));
            return true;
        }
        else {
            showError('Error', res?.error || 'Failed to save entry');
            return false;
        }
    }, [clientCompanyId, workspaceId, campaignId, level, showSuccess, showError, patchCategoryStatus, deriveCategoryPatch]);
    const deleteOverride = useCallback(async (category) => {
        setSaving(true);
        let res;
        if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.deleteWorkspaceOverride(clientCompanyId, workspaceId, category);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.deleteCampaignOverride(clientCompanyId, workspaceId, campaignId, category);
        }
        setSaving(false);
        if (res && !res.error) {
            showSuccess('Reverted', 'Override removed — inheriting from parent');
            setActiveEntry(null);
            fetchSummary();
            return true;
        }
        else {
            showError('Error', res?.error || 'Failed to remove override');
            return false;
        }
    }, [clientCompanyId, workspaceId, campaignId, level, showSuccess, showError, fetchSummary]);
    const uploadFile = useCallback(async (category, file, assetType) => {
        setSaving(true);
        let res;
        if (level === 'Company') {
            res = await knowledgeBaseApi.uploadCompanyFile(clientCompanyId, category, file, assetType);
        }
        else if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.uploadWorkspaceFile(clientCompanyId, workspaceId, category, file, assetType);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.uploadCampaignFile(clientCompanyId, workspaceId, campaignId, category, file, assetType);
        }
        setSaving(false);
        if (res?.data) {
            showSuccess('Uploaded', 'File uploaded');
            // Upload returns a single KnowledgeBaseFileViewDto, not the full entry.
            // Re-fetch the entry so activeEntry has the complete Files array.
            const refreshed = await fetchEntry(category);
            if (refreshed) {
                patchCategoryStatus(category, deriveCategoryPatch(refreshed));
            }
            return true;
        }
        else {
            showError('Error', res?.error || 'Failed to upload file');
            return false;
        }
    }, [clientCompanyId, workspaceId, campaignId, level, showSuccess, showError, patchCategoryStatus, deriveCategoryPatch, fetchEntry]);
    const deleteFile = useCallback(async (category, fileId) => {
        setSaving(true);
        let res;
        if (level === 'Company') {
            res = await knowledgeBaseApi.deleteCompanyFile(clientCompanyId, category, fileId);
        }
        else if (level === 'Workspace' && workspaceId) {
            res = await knowledgeBaseApi.deleteWorkspaceFile(clientCompanyId, workspaceId, category, fileId);
        }
        else if (level === 'Campaign' && workspaceId && campaignId) {
            res = await knowledgeBaseApi.deleteCampaignFile(clientCompanyId, workspaceId, campaignId, category, fileId);
        }
        setSaving(false);
        if (res && !res.error) {
            showSuccess('Removed', 'File removed');
            const updatedEntry = await fetchEntry(category);
            if (updatedEntry) {
                patchCategoryStatus(category, deriveCategoryPatch(updatedEntry));
            }
            return true;
        }
        else {
            showError('Error', res?.error || 'Failed to delete file');
            return false;
        }
    }, [clientCompanyId, workspaceId, campaignId, level, showSuccess, showError, patchCategoryStatus, deriveCategoryPatch, fetchEntry]);
    return {
        summary,
        categories: (summary?.Categories ?? []),
        loading,
        error,
        refetchSummary: fetchSummary,
        activeEntry,
        entryLoading,
        fetchEntry,
        upsertEntry,
        deleteOverride,
        uploadFile,
        deleteFile,
        saving,
    };
}
